import axios, { AxiosResponse } from 'axios';
import {
  User,
  UserOnboardingStep,
  GCUPayload,
  ValidateOnboardingStepPayload,
  UserZoneOfSearchPayload,
  ProfessionalExperience,
  UserProgram,
  Program,
  MailQueue,
  Mail,
  ApiRequestLog,
} from '@addviseo/shared/types';

export const logout = () => {
  return axios
    .post<AxiosResponse<{ message: string; redirect: string }>>('/api/authentication/logout')
    .then((response) => response.data);
};

export const getMe = () => {
  return axios.get<User>('/api/authentication/me').then((response) => response.data);
};

export const list = ({ search }: { search: string }) => {
  return axios
    .get<User[]>('/api/users/list', { params: { search } })
    .then((response) => response.data);
};

export const acceptGCU = (accepted: boolean) => {
  return axios.post<GCUPayload, AxiosResponse<{ code: string; message: string }>>(
    '/api/user/accept-gcu',
    { accepted },
  );
};

export const validateOnboardingStep = (step: UserOnboardingStep) => {
  return axios
    .post<ValidateOnboardingStepPayload, AxiosResponse<{ step: UserOnboardingStep }>>(
      '/api/user/onboarding/validate-step',
      { step },
    )
    .then((response) => response.data);
};

export const addBeneficiaryZoneOfSearch = async (params: UserZoneOfSearchPayload) => {
  return axios
    .post<UserZoneOfSearchPayload>('/api/user/zone-of-search', params)
    .then((response) => response.data);
};

export const updateBeneficiaryZoneOfSearch = async (params: UserZoneOfSearchPayload) => {
  return axios
    .put<UserZoneOfSearchPayload>(`/api/user/zone-of-search/`, params)
    .then((response) => response.data);
};

export const getUserProfessionalExperiences = async () => {
  return axios
    .get<ProfessionalExperience[]>('/api/user/professional-experiences')
    .then((response) => response.data);
};

export const getCVDesignrMagicLink = async (
  payload: { userId?: number; cv?: string } = {},
): Promise<{ magicLink: string }> => {
  return axios
    .post<{ magicLink: string }>('/api/cvdesignr/magic-link', payload)
    .then((response) => response.data);
};

export type RequestCVDesignrAuthorizationResponse =
  | { status: 'link'; authorizationLink: string }
  | { status: 'already_associated' };

export const syncAddviseoV1 = (userId: number) => {
  return axios
    .post<{ ok: true }>(`/api/users/${userId}/sync-addviseo-v1`)
    .then((response) => response.data);
};

export interface UserSheetResponse {
  user: Pick<
    User,
    | 'id'
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'role'
    | 'status'
    | 'onboardingStep'
    | 'phoneNumber'
    | 'ide'
    | 'sieId'
    | 'addviseoId'
    | 'externalId'
  > & {
    createdAt: string;
    updatedAt: string;
    hasPassword: boolean;
  };
  userPrograms: Array<
    Pick<UserProgram, 'id' | 'addviseoUserId' | 'status'> & {
      createdAt: string;
      updatedAt: string;
      program: Pick<Program, 'id' | 'name' | 'addviseoId'>;
      advisor: Pick<User, 'id' | 'firstName' | 'lastName'> | null;
    }
  >;
}

export type UserMailItem = Pick<MailQueue, 'id' | 'emailId' | 'status' | 'params'> & {
  email: Pick<Mail, 'slug' | 'name'> | null;
  createdAt: string;
  updatedAt: string;
};

export type UserApiLogItem = Pick<
  ApiRequestLog,
  'requestId' | 'method' | 'url' | 'statusCode' | 'responseTime'
> & {
  createdAt: string;
  matchedVia: { urlPrefix: string; userField: string } | null;
};

export type ApiRequestLogFull = Pick<ApiRequestLog, 'requestId' | 'input' | 'output'>;

export const getUserSheet = (userId: number) =>
  axios.get<UserSheetResponse>(`/api/users/${userId}/sheet`).then((r) => r.data);

export const getUserMails = (userId: number) =>
  axios.get<{ items: UserMailItem[] }>(`/api/users/${userId}/mails`).then((r) => r.data);

export const getUserApiLogs = (userId: number) =>
  axios
    .get<{ items: UserApiLogItem[] }>(`/api/users/${userId}/api-request-logs`)
    .then((r) => r.data);

export const getApiRequestLogFull = (requestId: string) =>
  axios
    .get<ApiRequestLogFull>(`/api/api-request-logs/${encodeURIComponent(requestId)}/full`)
    .then((r) => r.data);

export const requestBeneficiaryCVDesignrAuthorization =
  async (): Promise<RequestCVDesignrAuthorizationResponse> => {
    return axios
      .post<RequestCVDesignrAuthorizationResponse>('/api/user/cvdesignr/request-authorization')
      .then((response) => response.data);
  };

export const updateProfileShared = async (profileShared: boolean) => {
  return axios
    .put<{ profileShared: boolean }, AxiosResponse<{ code: string }>>('/api/user/share-profile', {
      profileShared,
    })
    .then((response) => response.data);
};

export const reactivateAutoSharing = async () => {
  return axios
    .put<void, AxiosResponse<{ code: string }>>('/api/user/reactivate-auto-sharing')
    .then((response) => response.data);
};

export default {
  logout,
  getMe,
  acceptGCU,
  validateOnboardingStep,
  addBeneficiaryZoneOfSearch,
};
